import { useCallback, useEffect, useState } from "react";

/**
 * Manages how many “slots” are visible, up to an optional max.
 *
 * @param params.initialCount – how many to show on mount (clamped ≥0, ≤maxCount) (default: 0)
 * @param params.maxCount     – the upper bound on count (default: Infinity)
 * @returns An object with:
 * - count: the current number of visible slots
 * - increment: a function to increase the count by 1
 * - decrement: a function to decrease the count by 1
 * - canIncrement: true if count < maxCount
 * - canDecrement: true if count > minCount
 * - setVisibleCount: set to an exact number (clamped ≥0 and ≤maxCount)
 */
export function useVisibleCount({
  initialCount = 0,
  maxCount = Infinity,
  minCount = 0,
}: {
  initialCount?: number;
  maxCount?: number;
  minCount?: number;
}) {
  const clamp = (n: number) => Math.max(0, Math.min(n, maxCount));
  const [count, setCount] = useState(() => clamp(initialCount));

  useEffect(() => {
    setCount(clamp(initialCount));
  }, [initialCount, maxCount]);

  const increment = useCallback(() => {
    setCount((c) => clamp(c + 1));
  }, [maxCount]);

  const decrement = useCallback(() => {
    setCount((c) => clamp(c - 1));
  }, []);

  const setVisibleCount = useCallback(
    (n: number) => {
      setCount(clamp(n));
    },
    [maxCount],
  );

  const canIncrement = count < maxCount;
  const canDecrement = count > minCount;

  return {
    canDecrement,
    canIncrement,
    count,
    decrement,
    increment,
    setVisibleCount,
  } as const;
}
